<template>
  <span
    v-if="badge"
    class="inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium"
    :class="badgeClass">
    {{ displayValue }}
  </span>
  <span v-else :title="displayValue" :class="isDate ? 'whitespace-nowrap' : ''">
    {{ displayValue }}
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  item: { type: Object, required: true },
  header: { type: Object, required: true },
})

const enumLabels = {
  active: 'Ativo', inactive: 'Inativo', paused: 'Pausado', planned: 'Planejada',
  inProgress: 'Em andamento', atRisk: 'Em risco', blocked: 'Bloqueada', completed: 'Concluída', cancelled: 'Cancelada',
  received: 'Recebida', analysis: 'Em análise', estimation: 'Em estimativa', awaitingApproval: 'Aguardando aprovação',
  approved: 'Aprovada', rejected: 'Rejeitada', converted: 'Convertida', requested: 'Solicitada', resolved: 'Resolvido',
  superseded: 'Substituída', archived: 'Arquivada', vacation: 'Férias', dayOff: 'Folga', leave: 'Licença',
  unavailable: 'Indisponibilidade', local: 'Local', external: 'Externo', development: 'Desenvolvimento',
  homologation: 'Homologação', production: 'Produção', quickNote: 'Nota rápida', idea: 'Ideia',
  projectNote: 'Nota de projeto', followUp: 'Follow-up', cockpit: 'Cockpit', jira: 'Jira', github: 'GitHub',
  figma: 'Figma', online: 'Online', offline: 'Offline', error: 'Erro', unknown: 'Não verificado',
}

const booleanFields = new Set(['active', 'managed', 'exists', 'enabled', 'isActive'])
const dateOnlyFields = new Set(['startDate', 'endDate', 'dueDate', 'decidedAt'])
const dateTimeFields = new Set(['createdAt', 'updatedAt', 'occurredAt', 'lastCheckedAt', 'externalUpdatedAt', 'resolvedAt'])

const field = computed(() => String(props.header.field || ''))
const rawValue = computed(() => props.item?.[field.value])
const isBooleanField = computed(() => booleanFields.has(field.value))
const isDate = computed(() => dateOnlyFields.has(field.value) || dateTimeFields.has(field.value))
const isEnum = computed(() => ['status', 'type', 'source', 'origin', 'lastState'].includes(field.value))
const enumKey = computed(() => String(rawValue.value ?? ''))

const displayValue = computed(() => {
  const value = rawValue.value
  if (value === null || value === undefined || value === '') return '—'

  if (isBooleanField.value) {
    const truthy = value === true || value === 1 || value === '1' || value === 'true'
    if (field.value === 'active' || field.value === 'isActive') return truthy ? 'Ativo' : 'Inativo'
    if (field.value === 'managed') return truthy ? 'Gerenciado' : 'Não gerenciado'
    if (field.value === 'exists') return truthy ? 'Disponível' : 'Indisponível'
    return truthy ? 'Sim' : 'Não'
  }

  if (isDate.value) return formatDate(value, dateTimeFields.has(field.value))
  if (isEnum.value && enumLabels[enumKey.value]) return enumLabels[enumKey.value]
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
})

const badge = computed(() => isBooleanField.value || isEnum.value)

const badgeClass = computed(() => {
  const value = enumKey.value
  const truthy = rawValue.value === true || rawValue.value === 1 || rawValue.value === '1' || rawValue.value === 'true'

  if (isBooleanField.value) {
    return truthy ? 'border-cockpit-green/50 bg-cockpit-green/10 text-cockpit-green' : 'border-cockpit-red/50 bg-cockpit-red/10 text-cockpit-red'
  }
  if (['completed', 'approved', 'active', 'resolved', 'online', 'planned', 'local', 'cockpit'].includes(value)) {
    return 'border-cockpit-green/50 bg-cockpit-green/10 text-cockpit-green'
  }
  if (['blocked', 'cancelled', 'rejected', 'inactive', 'offline', 'error'].includes(value)) {
    return 'border-cockpit-red/50 bg-cockpit-red/10 text-cockpit-red'
  }
  if (['atRisk', 'paused', 'requested', 'analysis', 'estimation', 'awaitingApproval', 'unknown'].includes(value)) {
    return 'border-cockpit-orange/50 bg-cockpit-orange/10 text-cockpit-orange'
  }
  return 'border-cockpit-line text-cockpit-muted'
})

const formatDate = (value, withTime) => {
  const raw = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split('-')
    return `${day}/${month}/${year}`
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return raw
  return new Intl.DateTimeFormat('pt-BR', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' }).format(date)
}
</script>
